
import _ from 'lodash'

export default {
    props: {
        value: {
            type: [Object, String],
            default: null
        },
        id: {
            type: [String, Number],
            default: null
        },
        label: {
            type: [String, Boolean],
            default: false
        },
        readonly: {
            type: [String, Boolean],
            default: null
        },
        disabled: {
            type: [String, Boolean],
            default: null
        },
        placeholder: {
            type: [String],
            default: null
        },
        inputClass: {
            type: [String],
            default: null
        },
        groupClass: {
            type: [String],
            default: null
        },
        autocomplete: {
            type: [String, Boolean],
            default: null
        },
        tabindex: {
            type: [String, Number],
            default: null
        },
        showOneCounty: {
            type: Boolean,
            default: false
        },
        state: {
            type: Boolean,
            default: null,
        },
        required: {
            type: Boolean,
            default: true,
        },
    },
    data () {
        return {
            location: {},
            loading: false,
            counties: [],
        }
    },
    computed: {
        zipEntered () {
            const z = _.get(this.location, 'zip_code')
            return z && z.length === 5
        },
    },
    watch: {
        value (v1, v2) {
            if (!_.isEqual(v1, v2)) {
                this.updateValue()
            }
        },
        'location.zip_code' (v) {
            // Pasted zips often carry stray whitespace/characters (e.g. copied from a
            // spreadsheet cell), which leaves the value !== 5 valid digits and silently
            // blocks the counties/fips lookup. Strip to digits so paste behaves like typing.
            if (typeof v === 'string') {
                const clean = v.replace(/[^0-9]/g, '').slice(0, 5)
                if (clean !== v) {
                    this.$set(this.location, 'zip_code', clean)
                }
            }
        },
    },
    mounted () {
        this.setGlobal('$location', this)
        this.updateValue()
        this.$nextTick(async () => {
            await this.loadCounties()
            this.$emit('mounted')
            // delayed watching the zip to allow first automatic selected set by the template
            this.$watch('location.zip_code', () => this.loadCounties())
            this.$watch('location.fips', () => this.emitInput())
        })
    },

    methods: {
        updateValue () {
            const v = _.cloneDeep(this.value || {})
            if (this.value?.state === this.location?.state && this.value?.fips === this.location?.fips && this.value?.zip_code === this.location?.zip_code) {
                v.name = this.location?.name // preserve the name if the other fields are the same, to avoid unnecessary re-rendering of the county name in the UI which causes a jarring experience when it happens while the user is trying to select a county from the dropdown. This can be removed once we have a more robust way of preserving the county name in the case where the zip code doesn't change but the fips does, which is currently happening when the user selects a different county from the dropdown after entering a zip code that has multiple counties. The root cause of that issue is that when the user selects a different county from the dropdown, it updates the location.fips, which triggers this watcher, and if we don't preserve the name here, it will reset it to null because the value coming from props doesn't have the name field, which causes it to disappear from the UI until the next tick when it gets set again based on the new fips.
            }
            this.location = v
        },
        async loadCounties () {
            if (!this.zipEntered) {
                return null
            }
            this.$unalert()
            this.loading = true
            try {
                const resp = await this.$api.getCountiesForZip(_.get(this.location, 'zip_code'))
                if (resp) {
                    this.counties = resp.counties
                }
            } catch (e) {
                this.counties = []
                this.$alert(e.message)
            } finally {
                this.loading = false
            }

            // only auto-set the location.fips if it doesn't have one, or whatever it has is not in the last of available fips
            if (this.counties?.length && this.counties?.length === 1 && (!this.location?.fips || !_.find(this.counties, c => c.fips === this.location?.fips))) {
                this.$set(this.location, 'fips', _.get(this.counties, '0.fips'))
                this.$set(this.location, 'state', _.get(this.counties, '0.state'))
                this.$set(this.location, 'name', _.get(this.counties, '0.name'))
            } else if (this.counties?.length > 1 && this.location?.fips && !_.find(this.counties, c => c.fips === this.location?.fips)) {
                // if there are multiple counties, and the current fips is not in the list, reset it to null to force the user to select
                this.$set(this.location, 'fips', null)
                this.$set(this.location, 'state', null)
                this.$set(this.location, 'name', null)
            }

            this.emitInput()
        },
        emitInput () {
            if (this.location) {
                const county = _.find(this.counties, c => c.fips === this.location.fips)
                if (county) {
                    this.$set(this.location, 'fips', county.fips)
                    this.$set(this.location, 'state', county.state)
                    this.$set(this.location, 'name', county.name)
                }
            }
            this.$emit('input', _.cloneDeep(this.location))
        },
    },
}
